import { gql } from '@urql/core';

export const GET_DASHBOARD_ORDERS = gql`
  query GetDashboardOrders {
    orders(options: {
      take: 5,
      sort: { orderPlacedAt: DESC },
      filter: { orderPlacedAt: { isNull: false }, state: { notIn: ["AddingItems", "Draft", "ArrangingPayment"] } }
    }) {
      items {
        id
        code
        state
        totalWithTax
        currencyCode
        orderPlacedAt
        customer {
          firstName
          lastName
        }
      }
      totalItems
    }
  }
`;

export const GET_ACTIVE_CARTS = gql`
  query GetActiveCarts {
    orders(options: {
      take: 5,
      sort: { updatedAt: DESC },
      filter: { state: { eq: "AddingItems" }, totalQuantity: { gt: 0 } }
    }) {
      items {
        id
        code
        state
        totalWithTax
        currencyCode
        totalQuantity
        updatedAt
        customer {
          firstName
          lastName
        }
      }
      totalItems
    }
  }
`;

export const GET_SALES_REPORT = gql`
  query GetSalesReport($dateRange: DateRangeInput!, $compare: Boolean) {
    salesReport(dateRange: $dateRange, compare: $compare) {
      currencyCode
      moneyStrategyPrecision
      kpis {
        totalSales { value previousValue changePercent label }
        orderCount { value previousValue changePercent label }
        averageOrderValue { value previousValue changePercent label }
        itemsSold { value previousValue changePercent label }
      }
      salesOverTime {
        points { date value previousValue }
        total
        previousTotal
      }
      paymentMethodBreakdown { label value count percentage }
      shippingMethodBreakdown { label value count percentage }
      salesChannelBreakdown { label value count percentage }
      topCities { city province orderCount totalSales }
    }
  }
`;

export const GET_ORDERS_REPORT = gql`
  query GetOrdersReport($dateRange: DateRangeInput!, $compare: Boolean) {
    ordersReport(dateRange: $dateRange, compare: $compare) {
      currencyCode
      moneyStrategyPrecision
      kpis {
        totalOrders { value previousValue changePercent label }
        pendingOrders { value previousValue changePercent label }
        shippedOrders { value previousValue changePercent label }
        cancelledOrders { value previousValue changePercent label }
        avgFulfillmentHours { value previousValue changePercent label }
      }
      ordersOverTime {
        points { date value previousValue }
        total
        previousTotal
      }
      statusBreakdown { label value count percentage }
      paymentMethodBreakdown { label value count percentage }
      codVsPrepaid {
        codOrders
        codValue
        prepaidOrders
        prepaidValue
        codPercentage
      }
      fulfillmentMetrics {
        avgFulfillmentHours
        avgDeliveryHours
        cancellationRate
        onTimeDeliveryRate
      }
    }
  }
`;

export const GET_WIDGET_KPI = gql`
  query GetWidgetKpi($widgetId: String!, $dateRange: DateRangeInput!) {
    widgetKpi(widgetId: $widgetId, dateRange: $dateRange) {
      currencyCode
      moneyStrategyPrecision
      value
      previousValue
      changePercent
      label
    }
  }
`;

export const GET_WIDGET_TIME_SERIES = gql`
  query GetWidgetTimeSeries($widgetId: String!, $dateRange: DateRangeInput!, $compare: Boolean) {
    widgetTimeSeries(widgetId: $widgetId, dateRange: $dateRange, compare: $compare) {
      currencyCode
      moneyStrategyPrecision
      points { date value previousValue }
      total
      previousTotal
    }
  }
`;

export const GET_FINANCIAL_SUMMARY = gql`
  query GetFinancialSummary($dateRange: DateRangeInput!) {
    financialSummary(dateRange: $dateRange) {
      currencyCode
      moneyStrategyPrecision
      grossSales
      discounts
      netSales
      shipping
      tax
      refunds
      netRevenue
      totalCOGS
      grossProfit
      grossMarginPercent
    }
  }
`;

export const GET_LOYALTY_KPI_SUMMARY = gql`
  query GetLoyaltyKpiSummary($dateRange: DateRangeInput!) {
    loyaltyKpiSummary(dateRange: $dateRange) {
      pointsIssued
      pointsRedeemed
      pointsExpired
      pointsNet
      cashbackCredited
      cashbackRedeemed
      cashbackExpired
      cashbackNet
    }
  }
`;

export const GET_LOYALTY_LIABILITY_SUMMARY = gql`
  query GetLoyaltyLiabilitySummary {
    loyaltyLiabilitySummary {
      totalPointsOutstanding
      pointsLiabilityMinorUnits
      totalCashbackOutstanding
      combinedLiabilityMinorUnits
    }
  }
`;

export const GET_REWARD_SOURCE_SUMMARY = gql`
  query GetRewardSourceSummary {
    rewardSourceSummary {
      source
      currency
      issued
      reversed
      writtenOff
      netRetained
      issuedMinorUnits
      reversedMinorUnits
      writtenOffMinorUnits
      netRetainedMinorUnits
      recordCount
    }
  }
`;

export const GET_LOYALTY_TIME_SERIES = gql`
  query GetLoyaltyTimeSeries($dateRange: DateRangeInput!) {
    loyaltyTimeSeries(dateRange: $dateRange) {
      date
      earned
      redeemed
      expired
    }
  }
`;

export const GET_LOYALTY_TRANSACTION_BREAKDOWN = gql`
  query GetLoyaltyTransactionBreakdown($options: LoyaltyTransactionBreakdownOptions!) {
    loyaltyTransactionBreakdown(options: $options) {
      items {
        id
        createdAt
        customerEmail
        orderId
        type
        amount
        currency
        balance
        description
      }
      totalItems
    }
  }
`;

export const GET_LOYALTY_EXPORT_DATA = gql`
  query GetLoyaltyExportData($dateRange: DateRangeInput!) {
    loyaltyExportData(dateRange: $dateRange) {
      id
      createdAt
      customerEmail
      orderId
      type
      amount
      currency
      balance
      description
    }
  }
`;

export const GET_TOP_PRODUCTS = gql`
  query GetTopProducts($dateRange: DateRangeInput!, $limit: Int) {
    topProducts(dateRange: $dateRange, limit: $limit) {
      currencyCode
      moneyStrategyPrecision
      productId
      productName
      variantName
      unitsSold
      revenue
      totalCost
      grossProfit
      marginPercent
    }
  }
`;

export const GET_TOP_CUSTOMERS = gql`
  query GetTopCustomers($dateRange: DateRangeInput!, $limit: Int) {
    topCustomers(dateRange: $dateRange, limit: $limit) {
      currencyCode
      moneyStrategyPrecision
      customerId
      customerName
      orderCount
      totalSpent
    }
  }
`;

export const GET_LOW_STOCK_ITEMS = gql`
  query GetLowStockItems($threshold: Int, $limit: Int) {
    lowStockItems(threshold: $threshold, limit: $limit) {
      variantId
      sku
      productName
      stockOnHand
      stockAllocated
      available
    }
  }
`;

export const GET_PENDING_REVIEWS_COUNT = gql`
  query GetPendingReviewsCount {
    pendingReviewsCount
  }
`;

export const GET_ORDER_ACTION_COUNTS = gql`
  query GetOrderActionCounts {
    orderActionCounts {
      unfulfilled
      awaitingPayment
      pendingReturns
    }
  }
`;

export const GET_PRODUCT_REVIEW_ANALYTICS = gql`
  query GetProductReviewAnalytics($limit: Int) {
    productReviewAnalytics(limit: $limit) {
      totalReviewedProducts
      totalReviews
      averageRating
      coveragePercent
      topRated {
        productId
        productName
        reviewCount
        averageRating
        verifiedCount
        rating1
        rating2
        rating3
        rating4
        rating5
      }
      lowRated {
        productId
        productName
        reviewCount
        averageRating
        verifiedCount
        rating1
        rating2
        rating3
        rating4
        rating5
      }
      needsAttention {
        productId
        productName
        reviewCount
        averageRating
        verifiedCount
        rating1
        rating2
        rating3
        rating4
        rating5
      }
    }
  }
`;

export const GET_MERCHANT_GOALS = gql`
  query GetMerchantGoals {
    merchantGoals {
      currencyCode
      moneyStrategyPrecision
      id
      metric
      target
      period
      startDate
      endDate
      progressAvailable
      currentValue
      percentage
      remainingDays
      requiredDailyRate
    }
  }
`;

export const SET_MERCHANT_GOAL = gql`
  mutation SetMerchantGoal($input: SetMerchantGoalInput!) {
    setMerchantGoal(input: $input) {
      currencyCode
      moneyStrategyPrecision
      id
      metric
      target
      period
      startDate
      endDate
      progressAvailable
      currentValue
      percentage
      remainingDays
      requiredDailyRate
    }
  }
`;

export const GET_DASHBOARD_PREFERENCES = gql`
  query GetDashboardPreferences {
    dashboardPreferences {
      home { widgetId position size config }
      reports { widgetId position size config }
    }
  }
`;

export const SAVE_DASHBOARD_PREFERENCES = gql`
  mutation SaveDashboardPreferences($input: SaveDashboardPreferencesInput!) {
    saveDashboardPreferences(input: $input) {
      home { widgetId position size config }
      reports { widgetId position size config }
    }
  }
`;

export const GET_MERCHANT_PAYMENT_BALANCE = gql`
  query GetMerchantPaymentBalance($gateway: String!) {
    merchantPaymentBalance(gateway: $gateway) {
      currency
      total
      available
      transferred
    }
  }
`;

export const GET_MERCHANT_PAYMENT_TRANSACTIONS = gql`
  query GetMerchantPaymentTransactions(
    $gateway: String!
    $page: Int
    $status: String
    $createdAfter: String
    $createdBefore: String
  ) {
    merchantPaymentTransactions(
      gateway: $gateway
      page: $page
      status: $status
      createdAfter: $createdAfter
      createdBefore: $createdBefore
    ) {
      transactions {
        id
        status
        amount
        fee
        currency
        refunded
        description
        createdAt
        source {
          type
          company
          name
          number
        }
        metadata
      }
      totalCount
      currentPage
      totalPages
    }
  }
`;

export const GET_REPORT_MONEY_CONFIG = gql`
  query GetReportMoneyConfig { globalSettings { serverConfig { moneyStrategyPrecision } } }
`;
